import { Component, type ErrorInfo, type ReactNode } from "react";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

function isChunkLoadError(error: Error): boolean {
  return (
    error.name === "ChunkLoadError" ||
    error.message?.includes("Failed to fetch dynamically imported module") ||
    error.message?.includes("Importing a module script failed") ||
    error.message?.includes("Loading chunk") ||
    error.message?.includes("Loading CSS chunk")
  );
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, errorInfo: null };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    this.setState({ errorInfo: info });
    console.error("Unhandled error:", error, info.componentStack);

    // Auto-recover from chunk load failures (e.g. QUIC overload, stale deploy)
    if (isChunkLoadError(error)) {
      const retryKey = "chunk-load-retry";
      const alreadyRetried = sessionStorage.getItem(retryKey);
      if (!alreadyRetried) {
        sessionStorage.setItem(retryKey, "1");
        window.location.reload();
        return;
      }
      // Already retried once — fall through to error UI
      sessionStorage.removeItem(retryKey);
    }
  }

  render() {
    if (this.state.hasError) {
      const isChunk = this.state.error && isChunkLoadError(this.state.error);

      return (
        <div className="flex h-screen items-center justify-center bg-white">
          <div className="max-w-md text-center">
            <h1 className="mb-2 font-heading text-2xl font-bold text-text">
              {isChunk ? "Page failed to load" : "Something went wrong"}
            </h1>
            <p className="mb-6 font-body text-sm text-text-muted">
              {isChunk
                ? "A network issue prevented the page from loading. Please check your connection and try again."
                : "An unexpected error occurred. Please refresh the page to try again. If the problem persists, contact support."}
            </p>
            {this.state.error && !isChunk && (
              <details className="mb-4 text-left rounded-md border border-red-200 bg-red-50 p-3">
                <summary className="cursor-pointer font-body text-sm font-medium text-red-700">
                  Error details
                </summary>
                <pre className="mt-2 whitespace-pre-wrap break-words font-mono text-xs text-red-600">
                  {this.state.error.message}
                  {"\n\n"}
                  {this.state.error.stack}
                </pre>
              </details>
            )}
            {this.state.errorInfo?.componentStack && !isChunk && (
              <details className="mb-4 text-left rounded-md border border-amber-200 bg-amber-50 p-3">
                <summary className="cursor-pointer font-body text-sm font-medium text-amber-700">
                  Component stack
                </summary>
                <pre className="mt-2 whitespace-pre-wrap break-words font-mono text-xs text-amber-600">
                  {this.state.errorInfo.componentStack}
                </pre>
              </details>
            )}
            <button
              onClick={() => window.location.reload()}
              className="rounded-md bg-teal px-6 py-2 font-heading text-sm font-semibold text-white transition-colors hover:bg-teal/90"
            >
              Refresh Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
